import sortBy from "lodash/sortBy";
import { PER_PAGE_UNPAGINATED } from "PFApp/constants/unpaginated.js";
import { LoadingDots } from "PFComponents/loading_dots";
import NotFoundComponent from "PFComponents/not_found/not_found";
import { PageWrapper } from "PFComponents/page_wrapper";
import api from "PFCore/api";
import { useInternalExperiences } from "PFCore/hooks/queries/internal_experiences";
import { useCurrentProfileSet } from "PFCore/hooks/queries/profile";
import { useCurrentProfile } from "PFCore/hooks/queries/profile/use_current_profile";
import { useProfilesInvalidate } from "PFCore/hooks/queries/profiles/use_profiles_invalidate";
import { useSkillsFrameworksInvalidate } from "PFCore/hooks/queries/skills_frameworks/use_skills_frameworks_invalidate";
import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate, useParams } from "react-router";

import ProfileShowPage from "./profile_show_page";
import useInterests from "./query/use_interests";

const ProfileShowController = () => {
  const navigate = useNavigate();
  const { data: currentProfile } = useCurrentProfile();
  const { t } = useTranslation("profiles");
  const setCurrentProfile = useCurrentProfileSet();
  const { invalidateProfileSkillsFrameworks } = useSkillsFrameworksInvalidate();
  const { invalidate: invalidateProfiles } = useProfilesInvalidate();

  const { id, section } = useParams();

  useEffect(() => {
    if (id !== "me" && Number(id) === currentProfile.id) {
      navigate(`/profiles/me/${section ? `/${section}` : ""}`, { replace: true });
    }
  }, []);

  const [errors, setErrors] = useState(null);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    if (!currentProfile || !currentProfile.id) {
      return;
    }

    if (id === "me" || Number(id) === currentProfile.id) {
      setProfile(currentProfile);
    } else {
      api({ url: `profiles/${id}` })
        .then((resp) => setProfile(resp))
        .catch(() => setErrors([true]));
    }
  }, [currentProfile, id]);

  const {
    data: internalExperience,
    isLoading: isInternalExperienceLoading,
    refetch: refetchInternalExperience
  } = useInternalExperiences({
    profileId: id === "me" ? currentProfile.id : id,
    perPage: PER_PAGE_UNPAGINATED
  });

  const processedInternalExperience = useMemo(() => {
    const processedEntries = sortBy(
      (internalExperience?.entries || []).map(({ activity }) => {
        ["engagement_hours", "location"].forEach((field) => {
          if (!(field in activity.metadata)) {
            activity.metadata[field] = undefined;
          }
        });
        return activity;
      }),
      (obj) => {
        const dateRange = obj.metadata.date_range || {};
        if (!dateRange.start_date) {
          return Infinity;
        } else if (dateRange.current) {
          return -Infinity;
        } else {
          return -moment(dateRange.start_date);
        }
      }
    );

    return processedEntries;
  }, [internalExperience]);

  const interestsCollection = useInterests();

  if (profile?.suspendedAt) {
    return <Navigate to="/profiles" replace />;
  }

  if (!profile && !errors) {
    return <LoadingDots circlesEnabled pageViewEnabled />;
  }

  const handleProfileUpdate = (attributes = {}) => {
    const isMe = id === "me" || id === currentProfile.id;
    const url = isMe ? "me" : `profiles/${id}`;
    const noUpdate = Object.keys(attributes).length === 0;

    return api({
      url,
      method: noUpdate ? "GET" : "PUT",
      data: noUpdate ? null : attributes
    }).then((resp) => {
      setProfile(resp);
      if (profile.id === currentProfile.id) {
        setCurrentProfile(resp);
      }
      if (!noUpdate) {
        invalidateProfileSkillsFrameworks(profile.id);
        invalidateProfiles();
      }
      return resp;
    });
  };

  return (
    <PageWrapper
      isError={!!errors}
      isLoading={!errors && (isInternalExperienceLoading || !profile)}
      errorComponent={
        <NotFoundComponent
          message={t("profileDoesntExist")}
          buttonMessage={t("show.searchForExisting")}
          buttonUrl="/profiles"
        />
      }
    >
      <ProfileShowPage
        profile={profile || {}}
        onProfileUpdate={handleProfileUpdate}
        fetchInternalExperience={refetchInternalExperience}
        internalExperiences={processedInternalExperience}
        interestsCollection={interestsCollection}
        initSection={section}
      />
    </PageWrapper>
  );
};

ProfileShowController.propTypes = {
  type: PropTypes.string
};

export default ProfileShowController;
